import { useState, useEffect } from 'react'
import { paymentsApi } from '../../api/payments'
import { usersApi } from '../../api/users'
import { useUiStore } from '../../stores/uiStore'
import { Select } from '../ui/Select'
import type { User } from '../../types'

interface Plan {
  id: string
  name: string
  price_stars: number
  duration_days: number
}

interface PremiumStatus {
  is_premium: boolean
  expires_at?: string
  subscription_id?: string
}

interface Props {
  onBack: () => void
}

const PREMIUM_FEATURES = [
  { icon: '🚀', text: 'Increased upload size (4 GB)' },
  { icon: '⚡', text: 'Faster downloads' },
  { icon: '🎭', text: 'Exclusive sticker packs' },
  { icon: '🔒', text: 'No ads' },
  { icon: '📌', text: 'More pinned chats' },
  { icon: '🌟', text: 'Unique Premium badge' },
]

export function PremiumPanel({ onBack }: Props) {
  const [status, setStatus] = useState<PremiumStatus | null>(null)
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [subscribing, setSubscribing] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [showGift, setShowGift] = useState(false)
  const { addToast } = useUiStore()

  useEffect(() => {
    Promise.all([
      paymentsApi.getPremiumStatus(),
      paymentsApi.listPlans(),
    ])
      .then(([s, p]) => {
        setStatus(s)
        setPlans(p.plans ?? [])
      })
      .catch(() => addToast('error', 'Не удалось загрузить информацию о Premium'))
      .finally(() => setLoading(false))
  }, [addToast])

  const handleSubscribe = async (planId: string) => {
    setSubscribing(planId)
    try {
      const { subscription_id } = await paymentsApi.subscribe(planId)
      const plan = plans.find((p) => p.id === planId)
      const expiresAt = new Date(Date.now() + (plan?.duration_days ?? 30) * 86400000).toISOString()
      setStatus({ is_premium: true, expires_at: expiresAt, subscription_id })
      addToast('success', 'Premium активирован!')
    } catch {
      addToast('error', 'Не удалось оформить подписку')
    } finally {
      setSubscribing(null)
    }
  }

  const handleCancel = async () => {
    if (!status?.subscription_id) return
    setCancelling(true)
    try {
      await paymentsApi.cancelSubscription(status.subscription_id)
      setStatus({ is_premium: false })
      addToast('success', 'Подписка отменена')
    } catch {
      addToast('error', 'Не удалось отменить подписку')
    } finally {
      setCancelling(false)
    }
  }

  const formatExpiry = (iso?: string) => {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
  }

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">🌟 Premium</h2>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Status card */}
            {status?.is_premium ? (
              <div className="mx-4 mt-4 bg-gradient-to-br from-violet-500/20 to-purple-600/10 rounded-2xl p-5 border border-violet-500/20 text-center">
                <p className="text-4xl mb-2">👑</p>
                <p className="text-txt-primary font-bold text-lg">Premium Active</p>
                {status.expires_at && (
                  <p className="text-txt-meta text-sm mt-1">Renews {formatExpiry(status.expires_at)}</p>
                )}
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="mt-4 text-red-400 hover:text-red-300 text-sm disabled:opacity-40 transition-colors"
                >
                  {cancelling ? 'Cancelling…' : 'Cancel subscription'}
                </button>
              </div>
            ) : (
              <div className="mx-4 mt-4 bg-gradient-to-br from-violet-500/20 to-purple-600/10 rounded-2xl p-5 border border-violet-500/20 text-center">
                <p className="text-4xl mb-2">🌟</p>
                <p className="text-txt-primary font-bold text-lg">Get Premium</p>
                <p className="text-txt-meta text-sm mt-1">Unlock exclusive features</p>
              </div>
            )}

            {/* Feature list */}
            <div className="mx-4 mt-4">
              <p className="text-txt-meta text-xs uppercase tracking-wider font-medium mb-2">What's included</p>
              <div className="bg-bg-input rounded-2xl divide-y divide-divider overflow-hidden">
                {PREMIUM_FEATURES.map((f) => (
                  <div key={f.text} className="flex items-center gap-3 px-4 py-3">
                    <span className="text-xl shrink-0">{f.icon}</span>
                    <p className="text-txt-primary text-sm">{f.text}</p>
                    {status?.is_premium && (
                      <svg className="ml-auto text-green-400 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Plans */}
            {!status?.is_premium && plans.length > 0 && (
              <div className="mx-4 mt-4 mb-4">
                <p className="text-txt-meta text-xs uppercase tracking-wider font-medium mb-2">Choose a plan</p>
                <div className="space-y-2">
                  {plans.map((plan) => (
                    <div key={plan.id} className="bg-bg-input rounded-2xl px-4 py-3 flex items-center gap-3 border border-divider">
                      <div className="flex-1 min-w-0">
                        <p className="text-txt-primary text-sm font-medium">{plan.name}</p>
                        <p className="text-txt-meta text-xs">{plan.duration_days} days</p>
                      </div>
                      <button
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={subscribing === plan.id}
                        className="bg-violet-500 hover:bg-violet-400 text-white px-4 py-1.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-40 shrink-0"
                      >
                        {subscribing === plan.id ? '…' : `${plan.price_stars} ⭐`}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!status?.is_premium && plans.length === 0 && (
              <div className="text-center py-6 px-4">
                <p className="text-txt-meta text-sm">No plans available right now</p>
              </div>
            )}

            {/* Gift Premium */}
            {plans.length > 0 && (
              <div className="mx-4 mb-4">
                <button
                  onClick={() => setShowGift((s) => !s)}
                  className="w-full text-left px-4 py-3 bg-bg-input rounded-2xl border border-divider flex items-center gap-2 hover:bg-bg-hover transition-colors"
                >
                  <span className="text-xl">🎁</span>
                  <div className="flex-1">
                    <p className="text-txt-primary text-sm font-medium">Gift Premium</p>
                    <p className="text-txt-meta text-xs">Send a premium subscription to a friend</p>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`text-txt-meta transition-transform ${showGift ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {showGift && (
                  <GiftPremiumForm plans={plans} onDone={() => setShowGift(false)} />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function GiftPremiumForm({ plans, onDone }: { plans: Plan[]; onDone: () => void }) {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<User[]>([])
  const [recipient, setRecipient] = useState<User | null>(null)
  const [planId, setPlanId] = useState(plans[0]?.id ?? '')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const { addToast } = useUiStore()

  const handleSearch = async (q: string) => {
    setSearch(q)
    if (!q.trim()) { setResults([]); return }
    try {
      const r = await usersApi.search(q)
      setResults(r.users ?? [])
    } catch { /* ignore */ }
  }

  const handleGift = async () => {
    if (!recipient || !planId) return
    setSending(true)
    try {
      await paymentsApi.giftPremium(recipient.id, planId, message || undefined)
      addToast('success', `🎁 Premium gifted to ${recipient.first_name || recipient.username}!`)
      onDone()
    } catch {
      addToast('error', 'Не удалось подарить Premium')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="mt-2 bg-bg-primary border border-divider rounded-2xl p-4 space-y-3">
      {/* Recipient */}
      {recipient ? (
        <div className="flex items-center gap-2 bg-bg-input rounded-xl px-3 py-2">
          <span className="text-txt-primary text-sm flex-1">{recipient.first_name || recipient.username}</span>
          <button onClick={() => setRecipient(null)} className="text-txt-meta text-xs hover:text-txt-secondary">✕</button>
        </div>
      ) : (
        <div className="relative">
          <input
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Поиск получателя…"
            className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
          />
          {results.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-bg-primary border border-divider rounded-xl z-10 shadow-xl max-h-32 overflow-y-auto">
              {results.slice(0, 4).map((u) => (
                <button
                  key={u.id}
                  onClick={() => { setRecipient(u); setSearch(''); setResults([]) }}
                  className="w-full text-left px-3 py-2 text-txt-primary text-sm hover:bg-bg-hover"
                >
                  {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.username}
                  {u.username && <span className="text-txt-meta text-xs ml-1">@{u.username}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Plan select */}
      <Select
        value={planId}
        onChange={setPlanId}
        className="w-full"
        options={plans.map((p) => ({ value: p.id, label: `${p.name} — ${p.price_stars} ⭐` }))}
      />

      {/* Optional message */}
      <input
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Add a message (optional)"
        maxLength={200}
        className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
      />

      <div className="flex gap-2">
        <button onClick={onDone} className="flex-1 bg-bg-input text-txt-secondary py-2 rounded-xl text-sm hover:bg-bg-hover transition-colors">Cancel</button>
        <button
          onClick={handleGift}
          disabled={sending || !recipient || !planId}
          className="flex-1 bg-violet-500 hover:bg-violet-400 text-white py-2 rounded-xl text-sm font-medium disabled:opacity-40 transition-colors"
        >
          {sending ? 'Gifting…' : '🎁 Gift Premium'}
        </button>
      </div>
    </div>
  )
}
